import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

const ALL_OPPORTUNITY_DEALS_FETCH_LIMIT = 500;

export const offerQueryKeys = {
  all: ['offers'] as const,
  details: () => [...offerQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...offerQueryKeys.details(), id] as const,
  opportunityDeals: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'deals'] as const,
  opportunityRelated: (id: string | undefined) => [...offerQueryKeys.all, 'opportunity', id, 'related'] as const,
  opportunity: (id: string) => [...offerQueryKeys.all, 'opportunity', id] as const,
};

export type OpportunityRoomAsset = {
  id?: string | number | null;
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  maximum_occupancy_number__c?: string | number | null;
  name?: string | null;
  room_size__c?: string | number | null;
  sfid?: string | null;
};

export type OpportunityDeal = {
  bed_groups?: Array<{
    configuration?: Array<{ quantity?: number; type?: string }>;
  }>;
  id: string | number;
  cost_price__c?: string | number | null;
  currencyisocode?: string | null;
  extra_night_margin__c?: number | null;
  fk_room_type_id?: string | null;
  margin_percent__c?: string | number | null;
  name?: string | null;
  price__c?: string | number | null;
  rack_rate__c?: string | number | null;
  room_asset?: OpportunityRoomAsset | null;
  room_size__c?: string | number | null;
  sfid?: string | null;
  suggested_value__c?: number | null;
};

export type OpportunityRoom = OpportunityDeal;

export function useGetOffer(offerId: string | undefined, options?: { retain_dynamic_tags: boolean }) {
  return useQuery({
    queryKey: options ? [...offerQueryKeys.detail(offerId), options] : offerQueryKeys.detail(offerId),
    queryFn: async () => {
      const response = await OffersService.getOffer(offerId, options);
      return response.result;
    },
    enabled: !!offerId,
  });
}

export function useGetOfferSchema(offerId: string | undefined) {
  return useQuery({
    queryKey: [...offerQueryKeys.detail(offerId), 'schema'],
    queryFn: async () => {
      const response = await OffersService.getOfferSchema(offerId);
      return response.patch.body.schema;
    },
    enabled: !!offerId,
  });
}

export function useGetRoomPolicySchema(offerId: string | undefined, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...offerQueryKeys.detail(offerId), 'room-policies', 'schema'],
    queryFn: async () => {
      const response = await OffersService.getRoomPolicySchema(offerId);
      return response.schema;
    },
    enabled: enabled && !!offerId,
  });
}

export function useGetOpportunity<TOpportunity = App.OpportunityPage>(
  opportunityId: string | undefined,
  { enabled = false }: { enabled?: boolean } = {},
) {
  return useQuery<TOpportunity>({
    queryKey: offerQueryKeys.opportunity(opportunityId ?? ''),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getOpportunity(opportunityId);
      return response.result as TOpportunity;
    },
    enabled: enabled && !!opportunityId,
  });
}

export function useGetOpportunityDeals(
  opportunityId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: offerQueryKeys.opportunityDeals(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getDeals(opportunityId, 1, ALL_OPPORTUNITY_DEALS_FETCH_LIMIT);
      return response.result as Array<OpportunityDeal>;
    },
    enabled: enabled && !!opportunityId,
  });
}

export function useUpdateOffer(offerId: string, options?: { invalidateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      return OffersService.updateOffer(data, offerId);
    },
    onSuccess: () => {
      const shouldInvalidate = options?.invalidateOnSuccess ?? true;

      if (!shouldInvalidate) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}

export interface StructuredFinePrint {
  cancellation_text: string;
  date_changes_text: string;
  inclusion_notes: string;
  fine_print: string;
}

export interface StructuredFacilityItem {
  code: string;
  name: string;
  icon?: string | null;
  displayOrder?: number | null;
}

export interface StructuredFacilityGroup {
  group: {
    code: string;
    name: string;
    icon?: string | null;
    displayOrder?: number | null;
  };
  facilities: Array<StructuredFacilityItem>;
}

export function useUpdateStructuredFinePrint(offerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StructuredFinePrint) => {
      return OffersService.updateOffer({ structured_fine_print: data }, offerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}
